<template>
  <div class="news-codes">
    <div class="tree-wrapper">
      <CodesTree
        ref="codesTree"
        :items="items"
        :included="includedIds"
        :disabled="disabledIdsForSelection"
        selectMode="strict"
        class="codes-tree"
        @change="onChangeSelection"
      />
    </div>
    <div
      v-if="selectedCodes?.length"
      class="selected-codes"
    >
      <div class="selected-title">
        Добавляемые коды{{ selectedCodes.length > 0 ? ` (${selectedCodes.length})` : '' }}:
      </div>
      <div class="codes-list">
        <div
          v-for="code of selectedCodes"
          :key="code.id"
          :title="formatCodePath(code.path)"
          class="included"
        >
          <span>{{ code.name }}</span>
          <PsIcon
            name="close"
            size="14"
            class="remove-code"
            @click="uncheckItem(code.id)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, useTemplateRef } from 'vue'
import { cloneDeep } from 'lodash-es'
import CodesTree from './codesTree/CodesTree.vue'
import PsIcon from '@/ContextTab/components/UI/PsIcon.vue'
import { EQuadboxState } from '@/ContextTab/components/UI/types'
import { filterCodesTree, formatCodePath } from '@/lib/codes'
import { useCodesStore } from '@/ContextTab/stores/codes'
import { ELanguage, ELanguageReverse } from '@/lib/references'
import type { CodeSelectState } from '@/ContextTab/modules/news/codes/types'
import type { NewsItemPublished } from '@/ContextTab/stores/types'


const props = defineProps<{
  item: NewsItemPublished
}>()

const emit = defineEmits<{
  (e: 'update', payload: CodeSelectState): void
}>()

const codesTree = useTemplateRef<InstanceType<typeof CodesTree>>('codesTree')

const codesStore = useCodesStore()

const selectState = ref<CodeSelectState>({})

const newsLanguage = computed(() => ELanguage[props.item?.version?.language_id ?? ELanguageReverse.RU])

const versionCodeIds = computed(() =>
  props.item?.version?.codes?.map(({ code }) => code.id) ?? [],
)

const includedIds = computed(() =>
  Object.keys(selectState.value).map((id) => Number(id)),
)

const selectedCodes = computed(() =>
  Object.keys(selectState.value).map((id) => codesStore.codesMap[id]),
)

const items = computed(() =>
  filterCodesTree(
    codesStore.codesForRepublication.codes[newsLanguage.value] ?? [],
    (item) => !versionCodeIds.value.includes(item.id),
    false,
  ),
)

const disabledIdsForSelection = computed(() => codesStore.codesForRepublication.disabledIds)

function onChangeSelection(newSelectState: CodeSelectState) {
  // left only selected
  selectState.value = Object.fromEntries(
    Object.entries(newSelectState)
      .filter(([_, state]) => state === EQuadboxState.included),
  )
  emit('update', cloneDeep(selectState.value))
}

function uncheckItem(id: number) {
  codesTree.value?.uncheckItem(id)
}
</script>

<style scoped>
.news-codes {
  display: flex;
  flex-direction: column;
  height: 100%;

  .tree-wrapper {
    flex-grow: 1;
    overflow: hidden;
    height: 100%;

    .codes-tree {
      height: 100%;
    }
  }

  .selected-codes {
    margin-bottom: var(--ps-padding-m);

    .selected-title {
      padding: var(--ps-padding-m) 0;
    }

    .codes-list {
      max-height: 80px;
      overflow-y: auto;

      .included {
        display: inline-flex;
        align-items: center;
        margin: 0 var(--ps-padding-s) var(--ps-padding-s) 0;
        padding: var(--ps-padding-xs) var(--ps-padding-s);
        border-radius: var(--ps-border-radius-m);
        font-size: var(--ps-font-size-s);
        line-height: normal;
        border: 1px solid #9dbccd;
        background: #d9ebfb;
      }

      .remove-code {
        cursor: pointer;
        margin-left: var(--ps-padding-xs);
      }
    }
  }
}
</style>
